import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { listCollections, type Collection } from "../api";
import { useT } from "../i18n";
import { initialOf, rampFor } from "./cover";

/**
 * CollectionRow is Browse's row of curated collections, above the grid
 * (requirement 0119). The server sends only collections holding a book this
 * viewer may read, counted and covered over those books alone, so the row
 * never needs to know who is looking. Nothing to show — none, still loading,
 * or a failure — shows nothing: the grid underneath is the page.
 */
export default function CollectionRow() {
  const t = useT();
  const { data } = useQuery({ queryKey: ["collections"], queryFn: listCollections });
  const items = data?.items ?? [];
  if (items.length === 0) return null;
  return (
    <section data-testid="collection-row" className="mb-6">
      <h2 className="mb-2 text-sm font-semibold text-muted">{t.collections.row}</h2>
      <ul className="-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2">
        {items.map((c) => (
          <CollectionCard key={c.id} collection={c} />
        ))}
      </ul>
    </section>
  );
}

function CollectionCard({ collection: c }: { collection: Collection }) {
  const t = useT();
  const [from, to] = rampFor(c.id);
  return (
    <li data-testid="collection-card" className="group w-32 shrink-0 snap-start">
      <Link
        to={`/collections/${c.id}`}
        className="block rounded-card focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
      >
        <div className="aspect-[3/4] overflow-hidden rounded-card bg-sunk ring-1 ring-ink/5">
          {c.cover_url ? (
            <img
              src={c.cover_url}
              alt=""
              width={c.cover_width}
              height={c.cover_height}
              loading="lazy"
              className="size-full object-cover"
            />
          ) : (
            <div
              className="grid size-full place-items-center"
              style={{ background: `linear-gradient(150deg, ${from}, ${to})` }}
            >
              <span aria-hidden="true" className="text-4xl font-semibold text-white/85 select-none">
                {initialOf(c.name)}
              </span>
            </div>
          )}
        </div>
        <h3
          dir="auto"
          className="mt-1.5 line-clamp-2 text-sm leading-snug font-semibold group-hover:text-sage-deep"
        >
          {c.name}
        </h3>
        <p data-testid="collection-count" className="text-xs text-muted">
          {c.kind === "series" ? t.series.volumes(c.count) : t.collections.books(c.count)}
        </p>
      </Link>
    </li>
  );
}
